import { ChooseEntityConfig } from "./choose-entity-config-context";

import { Entity, EntityType } from "@/queries";

export type ChooseEntityDialogTriggerSize = "xs" | "sm" | "md" | "lg";

// Base props shared by both modes
export interface BaseChooseEntityDialogProps<T extends EntityType = EntityType> {
  type: T;
  currentFolderId: number | null;
  size?: ChooseEntityDialogTriggerSize;
  readonly?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: Entity[] | ((entity: Entity<T>) => boolean);
  isLoading?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  className?: string;
  config?: Partial<Omit<ChooseEntityConfig, "mode">>;
}

// Single selection mode
export interface SingleSelectProps<T extends EntityType = EntityType>
  extends BaseChooseEntityDialogProps<T> {
  mode?: "single";
  onSelect: (entity: Entity<T>) => void;
  selectedEntity?: Entity<T>;
}

// Multi selection mode
export interface MultiSelectProps<T extends EntityType = EntityType>
  extends BaseChooseEntityDialogProps<T> {
  mode: "multi";
  onSelect?: (entities: Entity<T>[]) => void;
  selectedEntities?: Entity<T>[];
  min?: number;
  max?: number;
}

// Discriminated union
export type ChooseEntityDialogProps<T extends EntityType = EntityType> =
  | SingleSelectProps<T>
  | MultiSelectProps<T>;

// Type guards
export const isSingleSelectProps = <T extends EntityType = EntityType>(
  props: ChooseEntityDialogProps<T>,
): props is SingleSelectProps<T> => !props.mode || props.mode === "single";

export const isMultiSelectProps = <T extends EntityType = EntityType>(
  props: ChooseEntityDialogProps<T>,
): props is MultiSelectProps<T> => props.mode === "multi";
